import idGen from "../../utils/idGen";

// The row of a block with an _id on each sub-block. The editor finds a
// sub-block by its _id (e.g. the delayed hand-off of a conditions box), but
// many stored sub-blocks have none: they get one from newId(row, index), by
// default the generator of a new sub-block. An existing _id is kept; a row
// whose sub-blocks all have one is returned as it is (the same object).
//
// Applied each time the form's item is derived from the stored row, newId
// must be deterministic (the same row gives the same ids, e.g.
// (row, index) => `${row.id}_${index}`): random ids make each derivation
// differ from the form's initial values, and Formik then reinitialises the
// form, discarding its unsaved changes.
export function withSubBlockIds(row, newId = idGen) {
  const blocks = row?.blocks;
  if (!Array.isArray(blocks) || blocks.every((block) => !block || block._id))
    return row;

  return {
    ...row,
    blocks: blocks.map((block, index) =>
      block && !block._id ? { ...block, _id: newId(row, index) } : block
    )
  };
}

// The deterministic generator for the stored sub-blocks without an _id:
// `${row.id}_${index}`, the same at every derivation of the same row, and
// unique in the block (in the play-prod-v2 blocks, a block has ids on all
// its sub-blocks or on none; a suffix keeps a mixed one unique all the same).
// The block ids are identifiers (idGen): so is the _id.
export function storedSubBlockId(row, index) {
  const taken = new Set((row.blocks ?? []).map((block) => block?._id));
  let id = `${row.id}_${index}`;
  while (taken.has(id)) id = `${id}_`;
  return id;
}

// The row of a stored block with an _id on each sub-block, the same ids at
// every read of the same row (the item of the block form, see
// hooks/itemRow.js). The ids reach the stored row with the next Save.
export const withStoredSubBlockIds = (row) =>
  withSubBlockIds(row, storedSubBlockId);

// The key by which the editor finds a sub-block of `blocks` again later (the
// delayed writes of a sub-block: they may come after a delete or a move of
// the sub-blocks): its _id, or for the sub-blocks of a page, which have none
// and get none (hooks/itemRow.js), their `id` (the key of their layout).
// Undefined when it has neither, or when another sub-block of `blocks` has
// the same key (in play-prod-v2 the ids are unique in each page: 316 pages,
// 578 sub-blocks): the writes then go by index (subBlockIndex).
export function subBlockKey(blocks, block) {
  const key = block?._id ?? block?.id;
  if (key === undefined || key === null || key === "") return undefined;

  let count = 0;
  for (const other of blocks ?? []) if ((other?._id ?? other?.id) === key) count++;
  return count > 1 ? undefined : key;
}

// The index in `blocks` of the sub-block of key `key` (subBlockKey), wherever
// it is now; -1 if it is gone. Without a key, `index`, if there is a
// sub-block there.
export function subBlockIndex(blocks, key, index) {
  if (key === undefined) return index >= 0 && index < blocks.length ? index : -1;
  return blocks.findIndex((block) => (block?._id ?? block?.id) === key);
}
